import React, { useEffect, useState, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Search, Briefcase, Building2, Users, X, ArrowRight, Clock, AlertCircle, CheckCircle, Loader2, Zap, Calendar, Link2, Palette, UserCircle, Navigation, ScanSearch } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import api from '../api/axios'
import { useAuth } from '../context/AuthContext'

// Highlight matched text in results
function Highlight({ text, query }: { text: string, query: string }) {
  if (!query.trim() || !text) return <>{text}</>
  const searchTerm = query.trim().toLowerCase()
  const index = text.toLowerCase().indexOf(searchTerm)
  if (index === -1) return <>{text}</>
  return (
    <>
      {text.slice(0, index)}
      <mark className="bg-orange-200 dark:bg-orange-500/40 text-orange-800 dark:text-orange-200 rounded px-0.5 not-italic font-semibold">
        {text.slice(index, index + searchTerm.length)}
      </mark>
      {text.slice(index + searchTerm.length)}
    </>
  )
}

const STATUS_COLORS: Record<string, string> = {
  'Unallocated': 'bg-yellow-50 text-yellow-600',
  'Allocated': 'bg-blue-50 text-blue-600',
  'In Process': 'bg-blue-50 text-blue-600',
  'Send to QC': 'bg-purple-50 text-purple-600',
  'Completed': 'bg-green-50 text-green-600',
  'Correction': 'bg-red-50 text-red-600',
  'Paused': 'bg-gray-100 text-gray-500',
  'Carryover': 'bg-orange-50 text-orange-600',
}

interface SearchResults {
  jobs: any[]
  clients: any[]
  staff: any[]
}

interface QuickActions {
  unallocated?: number
  sendToQC?: number
  todayJobs?: number
  myJobs?: number
  myToday?: number
  expiringSubs?: number
}

export default function SmartSearch({ onClose }: { onClose: () => void }) {
  const { hasPermission, user } = useAuth()
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResults>({ jobs: [], clients: [], staff: [] })
  const [quickActions, setQuickActions] = useState<QuickActions>({})
  const [loading, setLoading] = useState(false)
  const [quickLoading, setQuickLoading] = useState(true)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [showHints, setShowHints] = useState(false)
  const [recentSearches, setRecentSearches] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('smart_search_recent') || '[]') } catch { return [] }
  })
  const [sessionViewed, setSessionViewed] = useState<any[]>([])
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [scope, setScope] = useState<'all' | 'jobs' | 'clients' | 'staff'>('all')
  const [ghostSuggestion, setGhostSuggestion] = useState('')
  const [suggestions, setSuggestions] = useState<{ text: string, category: string, icon: string }[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1)
  const [hintClients, setHintClients] = useState<{ abbreviation: string, companyName: string }[]>([])
  const [hintStaff, setHintStaff] = useState<{ name: string }[]>([])
  const [hoveredJobId, setHoveredJobId] = useState<string | null>(null)
  const [hoveredJobData, setHoveredJobData] = useState<any>(null)
  const hoverTimeout = useRef<any>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const searchTimeout = useRef<any>(null)

  const isArtist = ['artist', 'selfqc'].includes(user?.roleName || '')
  const isAdmin = user?.roleName === 'admin'

  // Focus input on mount
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 50)
  }, [])

  // Fetch hint data for suggestions
  useEffect(() => {
    api.get('/search/hints').then(res => {
      setHintClients(res.data.clients || [])
      setHintStaff(res.data.staff || [])
    }).catch(() => {})
  }, [])

  // Fetch quick actions
  useEffect(() => {
    api.get('/search/quick-actions').then(res => {
      setQuickActions(res.data)
      setQuickLoading(false)
    }).catch(() => setQuickLoading(false))
  }, [])

  // Debounced search
  const scopeRef = useRef<'all' | 'jobs' | 'clients' | 'staff'>('all')

  const doSearch = useCallback((q: string, currentScope: 'all' | 'jobs' | 'clients' | 'staff' = 'all') => {
    if (!q.trim()) { setResults({ jobs: [], clients: [], staff: [] }); return }
    setLoading(true)
    const cf = parseCombinedFilter(q)
    // Pure status: "completed jobs", "send to qc" → search with empty q + status filter
    // Combined: "MTY completed" → search client with status filter
    // Plain: "MTY" → search normally
    const searchQ = cf.isPureStatus ? '' : cf.isCombined ? cf.clientQuery : q
    let url = `/search?q=${encodeURIComponent(searchQ)}${currentScope !== 'all' ? `&type=${currentScope}` : ''}`
    if (cf.detectedStatus) url += `&status=${encodeURIComponent(cf.detectedStatus)}`
    if (cf.detectedType) url += `&requestType=${encodeURIComponent(cf.detectedType)}`
    api.get(url).then(res => {
      setResults(res.data)
      setSelectedIndex(0)
    }).catch(() => {}).finally(() => setLoading(false))
  }, [])

  const saveRecentSearch = (q: string) => {
    if (!q.trim() || q.trim().length < 2) return
    setRecentSearches(prev => {
      const updated = [q.trim(), ...prev.filter(s => s !== q.trim())].slice(0, 5)
      localStorage.setItem('smart_search_recent', JSON.stringify(updated))
      return updated
    })
  }

  const clearRecentSearches = () => {
    localStorage.removeItem('smart_search_recent')
    setRecentSearches([])
  }

  const fetchJobPreview = async (jobId: string) => {
    try {
      const res = await api.get(`/jobs/${jobId}`)
      setHoveredJobData(res.data)
    } catch {}
  }

  const copyJobCode = (e: React.MouseEvent, code: string, id: string) => {
    e.stopPropagation()
    navigator.clipboard.writeText(code)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 1500)
  }

  // Page navigation keywords — permission gated
  const PAGE_SHORTCUTS: { keyword: string, path: string, permission?: string, roles?: string[], excludeRoles?: string[] }[] = [
    { keyword: 'jobs', path: '/jobs', permission: 'access_jobs' },
    { keyword: 'job', path: '/jobs', permission: 'access_jobs' },
    { keyword: 'dashboard', path: '/' },
    { keyword: 'home', path: '/' },
    { keyword: 'staff', path: '/staff', permission: 'manage_staff' },
    { keyword: 'team', path: '/staff', permission: 'manage_staff' },
    { keyword: 'clients', path: '/clients', permission: 'manage_clients' },
    { keyword: 'client', path: '/clients', permission: 'manage_clients' },
    { keyword: 'reports', path: '/job-reports', permission: 'view_job_reports' },
    { keyword: 'report', path: '/job-reports', permission: 'view_job_reports' },
    { keyword: 'client reports', path: '/client-reports', permission: 'view_client_reports' },
    { keyword: 'revenue', path: '/revenue', excludeRoles: ['artist', 'selfqc', 'qc', 'hr', 'tl', 'client'] },
    { keyword: 'settings', path: '/settings', permission: 'manage_system_settings' },
    { keyword: 'finance', path: '/finance', roles: ['admin'] },
    { keyword: 'expenses', path: '/finance', roles: ['admin'] },
    { keyword: 'invoices', path: '/finance', roles: ['admin'] },
    { keyword: 'attendance', path: '/attendance' },
    { keyword: 'leave', path: '/leave' },
    { keyword: 'performance', path: '/performance' },
    { keyword: 'announcements', path: '/announcements' },
    { keyword: 'deals', path: '/inkdeals', permission: 'manage_inkdeals' },
    { keyword: 'inkdeals', path: '/inkdeals', permission: 'manage_inkdeals' },
    { keyword: 'plans', path: '/inkdeals', permission: 'manage_inkdeals' },
    { keyword: 'support', path: '/support', permission: 'handle_client_support' },
    { keyword: 'onboarding', path: '/onboarding', permission: 'manage_onboarding' },
  ]

  const normalizedQuery = query.trim().toLowerCase()
    .replace(/^go to\s+/, '')
    .replace(/^open\s+/, '')
    .replace(/^navigate to\s+/, '')

  const matchedPageEntry = PAGE_SHORTCUTS.find(p => {
    if (p.keyword !== normalizedQuery) return false
    if (p.permission && !hasPermission(p.permission)) return false
    if (p.excludeRoles && p.excludeRoles.includes(user?.roleName || '')) return false
    return true
  })
  const matchedPage = matchedPageEntry?.path || null

  // Date shortcuts
  const DATE_SHORTCUTS: Record<string, { label: string, from: string, to: string }> = (() => {
    const now = new Date()
    const fmt = (d: Date) => d.toISOString().split('T')[0]
    const today = fmt(now)
    const yesterday = fmt(new Date(now.getTime() - 86400000))
    const weekStart = fmt(new Date(now.getTime() - now.getDay() * 86400000))
    const lastWeekStart = fmt(new Date(now.getTime() - (now.getDay() + 7) * 86400000))
    const lastWeekEnd = fmt(new Date(now.getTime() - (now.getDay() + 1) * 86400000))
    const monthStart = fmt(new Date(now.getFullYear(), now.getMonth(), 1))
    return {
      'today': { label: 'Today', from: today, to: today },
      'yesterday': { label: 'Yesterday', from: yesterday, to: yesterday },
      'this week': { label: 'This Week', from: weekStart, to: today },
      'thisweek': { label: 'This Week', from: weekStart, to: today },
      'last week': { label: 'Last Week', from: lastWeekStart, to: lastWeekEnd },
      'lastweek': { label: 'Last Week', from: lastWeekStart, to: lastWeekEnd },
      'this month': { label: 'This Month', from: monthStart, to: today },
      'thismonth': { label: 'This Month', from: monthStart, to: today },
    }
  })()
  const matchedDate = DATE_SHORTCUTS[query.trim().toLowerCase()]

  // Combined filter parser — extract client + status + type from query
  const parseCombinedFilter = (q: string) => {
    const lower = q.toLowerCase().trim()

    // Multi-word status phrases — check first before splitting
    const phraseStatusMap: Record<string, string> = {
      'jobs in process': 'In Process', 'in process jobs': 'In Process',
      'in process': 'In Process', 'inprocess': 'In Process',
      'send to qc jobs': 'Send to QC', 'jobs send to qc': 'Send to QC',
      'send to qc': 'Send to QC', 'sendtoqc': 'Send to QC',
      'query sent': 'Query Sent', 'query sent jobs': 'Query Sent',
      'carry over': 'Carryover', 'carry over jobs': 'Carryover',
      'unallocated jobs': 'Unallocated', 'jobs unallocated': 'Unallocated',
      'allocated jobs': 'Allocated', 'jobs allocated': 'Allocated',
      'completed jobs': 'Completed', 'jobs completed': 'Completed',
      'paused jobs': 'Paused', 'jobs paused': 'Paused',
      'correction jobs': 'Correction', 'jobs correction': 'Correction',
      'carryover jobs': 'Carryover', 'jobs carryover': 'Carryover',
      'dismissed jobs': 'Dismissed', 'jobs dismissed': 'Dismissed',
    }

    // Check if entire query is a pure phrase status
    if (phraseStatusMap[lower]) {
      return { clientQuery: '', detectedStatus: phraseStatusMap[lower], detectedType: '', isCombined: false, isPureStatus: true }
    }

    // Check if query contains a multi-word status phrase — extract client prefix
    // e.g. "STR in process" → client="STR", status="In Process"
    // e.g. "STR send to qc" → client="STR", status="Send to QC"
    const multiWordPhrases: [string, string][] = [
      ['in process', 'In Process'],
      ['send to qc', 'Send to QC'],
      ['query sent', 'Query Sent'],
      ['carry over', 'Carryover'],
    ]
    for (const [phrase, status] of multiWordPhrases) {
      if (lower.includes(phrase)) {
        const clientQuery = lower.replace(phrase, '').replace(/\bjobs?\b/g, '').trim()
        return {
          clientQuery,
          detectedStatus: status,
          detectedType: '',
          isCombined: clientQuery.length > 0,
          isPureStatus: clientQuery.length === 0
        }
      }
    }

    const words = lower.split(/\s+/)
    const singleStatusMap: Record<string, string> = {
      'unallocated': 'Unallocated', 'allocated': 'Allocated',
      'active': 'In Process', 'inprocess': 'In Process',
      'qc': 'Send to QC', 'sendtoqc': 'Send to QC',
      'completed': 'Completed', 'done': 'Completed', 'paused': 'Paused',
      'carryover': 'Carryover', 'carry': 'Carryover',
      'correction': 'Correction', 'dismissed': 'Dismissed',
      'query': 'Query Sent', 'queried': 'Query Sent',
      'process': 'In Process', 'send': 'Send to QC',
    }
    const typeKeywords: Record<string, string> = {
      'fresh': 'fresh', 'revision': 'revision', 'correction': 'correction', 'complex': 'complex'
    }
    const noiseWords = new Set(['jobs', 'job', 'all', 'show', 'me', 'find', 'get', 'list'])

    let detectedStatus = ''
    let detectedType = ''
    const remainingWords: string[] = []

    for (const word of words) {
      if (singleStatusMap[word]) detectedStatus = singleStatusMap[word]
      else if (typeKeywords[word]) detectedType = typeKeywords[word]
      else if (!noiseWords.has(word)) remainingWords.push(word)
    }

    const clientQuery = remainingWords.join(' ').trim()
    return {
      clientQuery,
      detectedStatus,
      detectedType,
      isCombined: !!(detectedStatus || detectedType) && clientQuery.length > 0,
      isPureStatus: !!(detectedStatus || detectedType) && clientQuery.length === 0
    }
  }

  const combinedFilter = parseCombinedFilter(query)
  // ── NLP suggestion engine ──
  const buildGhostSuggestion = useCallback((q: string): string => {
    if (!q.trim() || q.length < 2) return ''
    const lower = q.toLowerCase().trim()

    // Status completions
    const statusMap: [string, string][] = [
      ['unalloc', 'unallocated'], ['alloc', 'allocated'],
      ['comp', 'completed'], ['done', 'completed'],
      ['send to q', 'send to qc'], ['sendtoq', 'send to qc'],
      ['in proc', 'in process'], ['inproc', 'in process'],
      ['corr', 'correction'], ['carry', 'carryover'],
      ['paus', 'paused'], ['quer', 'query sent'], ['dism', 'dismissed']
    ]
    for (const [prefix, full] of statusMap) {
      if (lower.startsWith(prefix) && !lower.startsWith(full)) return q + full.slice(lower.length)
    }

    // Time completions
    const timeMap: [string, string][] = [
      ['tod', 'today'], ['yest', 'yesterday'],
      ['this w', 'this week'], ['this m', 'this month'],
      ['last w', 'last week'], ['last m', 'last month'],
    ]
    for (const [prefix, full] of timeMap) {
      if (lower.startsWith(prefix) && !lower.startsWith(full)) return q + full.slice(lower.length)
    }

    // Product type completions
    const productMap: [string, string][] = [
      ['emb', 'embroidery'], ['med', 'medal'], ['lapy', 'lapel pin'],
      ['lap', 'lapel pin'], ['patc', 'patch'], ['lany', 'lanyard'],
      ['keyc', 'keychain'], ['buck', 'belt buckle'], ['charm', 'charm'],
      ['trop', 'trophy'], ['coin', 'coin'], ['mug', 'mug'],
    ]
    for (const [prefix, full] of productMap) {
      if (lower.startsWith(prefix) && !lower.startsWith(full)) return q + full.slice(lower.length)
    }

    // Combined pattern: "CLIENT status" — e.g. "MTY comp" → "MTY completed"
    const words = lower.split(/\s+/)
    if (words.length >= 2) {
      const lastWord = words[words.length - 1]
      for (const [prefix, full] of [...statusMap, ...timeMap, ...productMap]) {
        if (lastWord.startsWith(prefix) && !lastWord.startsWith(full)) {
          return q + full.slice(lastWord.length)
        }
      }
    }

    // Recent search completion
    const matchingRecent = recentSearches.find(r => r.toLowerCase().startsWith(lower) && r.toLowerCase() !== lower)
    if (matchingRecent) return matchingRecent

    // Page navigation completions
    const pageKeywords = ['jobs', 'clients', 'staff', 'revenue', 'settings', 'attendance', 'leave', 'reports', 'performance', 'support', 'dashboard']
    for (const kw of pageKeywords) {
      if (kw.startsWith(lower) && kw !== lower) return kw
    }

    return ''
  }, [recentSearches])

  // ── Full suggestion dropdown engine ──
  const buildSuggestions = useCallback((q: string) => {
    if (!q.trim() || q.trim().length < 1) { setSuggestions([]); setShowSuggestions(false); return }
    const lower = q.toLowerCase().trim()
    const matches: { text: string, category: string, icon: string }[] = []

    const statuses = ['unallocated', 'in process', 'send to qc', 'completed', 'correction', 'paused', 'carryover', 'query sent', 'dismissed']
    const times = ['today', 'yesterday', 'this week', 'last week', 'this month']
    const products = ['embroidery', 'medal', 'lapel pin', 'patch', 'lanyard', 'keychain', 'belt buckle', 'charm', 'trophy', 'coin', 'mug']
    const pages = ['jobs', 'clients', 'staff', 'revenue', 'settings', 'attendance', 'leave', 'reports', 'performance', 'support', 'dashboard']

    // Status based
    statuses.forEach(s => {
      if (s.includes(lower)) {
        matches.push({ text: s, category: 'Status', icon: 'status' })
        times.forEach(t => matches.push({ text: `${s} ${t}`, category: 'Status + Time', icon: 'time' }))
      }
    })

    times.forEach(t => {
      if (t.includes(lower)) {
        matches.push({ text: `${t}'s jobs`, category: 'Time', icon: 'time' })
        matches.push({ text: `my jobs ${t}`, category: 'My Jobs', icon: 'myjobs' })
      }
    })

    products.forEach(p => {
      if (p.includes(lower)) {
        matches.push({ text: `${p} jobs`, category: 'Product', icon: 'product' })
        times.forEach(t => matches.push({ text: `${p} jobs ${t}`, category: 'Product + Time', icon: 'product' }))
      }
    })

    if ('my jobs'.includes(lower) || lower.startsWith('my')) {
      matches.push({ text: 'my jobs', category: 'My Jobs', icon: 'myjobs' })
      matches.push({ text: 'my jobs today', category: 'My Jobs', icon: 'myjobs' })
      matches.push({ text: 'my jobs this week', category: 'My Jobs', icon: 'myjobs' })
      matches.push({ text: 'my unallocated', category: 'My Jobs', icon: 'myjobs' })
    }

    const upperQ = q.trim().toUpperCase()
    hintClients.forEach(c => {
      if (c.abbreviation && c.abbreviation.toUpperCase().startsWith(upperQ)) {
        matches.push({ text: c.abbreviation, category: `Client — ${c.companyName}`, icon: 'client' })
        statuses.forEach(s => matches.push({ text: `${c.abbreviation} ${s}`, category: `${c.companyName}`, icon: 'client' }))
        times.forEach(t => matches.push({ text: `${c.abbreviation} ${t}`, category: `${c.companyName}`, icon: 'client' }))
        matches.push({ text: `${c.abbreviation} completed this week`, category: `${c.companyName}`, icon: 'client' })
        matches.push({ text: `${c.abbreviation} unallocated`, category: `${c.companyName}`, icon: 'client' })
      }
    })

    hintStaff.forEach(s => {
      if (s.name.toLowerCase().includes(lower)) {
        matches.push({ text: s.name.toLowerCase(), category: 'Staff', icon: 'staff' })
        matches.push({ text: `${s.name.toLowerCase()} jobs today`, category: 'Staff', icon: 'staff' })
        matches.push({ text: `${s.name.toLowerCase()} completed`, category: 'Staff', icon: 'staff' })
        matches.push({ text: `${s.name.toLowerCase()} this week`, category: 'Staff', icon: 'staff' })
      }
    })

    pages.forEach(p => {
      if (p.includes(lower) || `go to ${p}`.includes(lower) || `open ${p}`.includes(lower)) {
        matches.push({ text: `go to ${p}`, category: 'Navigate', icon: 'navigate' })
      }
    })

    if ('expiring'.includes(lower) || 'subscription'.includes(lower) || lower.startsWith('exp') || lower.startsWith('sub')) {
      matches.push({ text: 'expiring subscriptions', category: 'Subscription', icon: 'sub' })
      matches.push({ text: 'active subscriptions', category: 'Subscription', icon: 'sub' })
    }

    recentSearches.forEach(r => {
      if (r.toLowerCase().includes(lower) && r.toLowerCase() !== lower) {
        matches.push({ text: r, category: 'Recent', icon: 'recent' })
      }
    })

    // Deduplicate and limit
    const seen = new Set<string>()
    const deduped = matches.filter(m => {
      if (seen.has(m.text)) return false
      seen.add(m.text)
      return true
    }).slice(0, 8)

    setSuggestions(deduped)
    setShowSuggestions(deduped.length > 0)
    setSelectedSuggestionIndex(-1)
  }, [hintClients, hintStaff, recentSearches])

  useEffect(() => {
    buildSuggestions(query)
  }, [query, buildSuggestions])

  // Update ghost suggestion whenever query changes
  useEffect(() => {
    setGhostSuggestion(buildGhostSuggestion(query))
  }, [query, buildGhostSuggestion])

  useEffect(() => {
    scopeRef.current = scope
    clearTimeout(searchTimeout.current)
    searchTimeout.current = setTimeout(() => doSearch(query, scope), 300)
    return () => clearTimeout(searchTimeout.current)
  }, [query, scope])

  // All result items flattened for keyboard nav
  const allItems = [
    ...results.jobs.map((j: any) => ({ type: 'job', data: j })),
    ...results.clients.map((c: any) => ({ type: 'client', data: c })),
    ...results.staff.map((s: any) => ({ type: 'staff', data: s })),
  ]

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        if (showSuggestions) {
          setSelectedSuggestionIndex(i => Math.min(i + 1, suggestions.length - 1))
        } else {
          setSelectedIndex(i => Math.min(i + 1, allItems.length - 1))
        }
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        if (showSuggestions) {
          setSelectedSuggestionIndex(i => Math.max(i - 1, -1))
        } else {
          setSelectedIndex(i => Math.max(i - 1, 0))
        }
      }
      if (e.key === 'Enter' && showSuggestions && selectedSuggestionIndex >= 0) {
        e.preventDefault()
        const picked = suggestions[selectedSuggestionIndex]
        setQuery(picked.text)
        setShowSuggestions(false)
        doSearch(picked.text, scopeRef.current)
        return
      }
      if ((e.key === 'Tab' || e.key === 'ArrowRight') && ghostSuggestion && document.activeElement === inputRef.current) {
        e.preventDefault()
        setQuery(ghostSuggestion)
        setGhostSuggestion('')
        doSearch(ghostSuggestion, scopeRef.current)
        return
      }
      if (e.key === 'Tab') {
        e.preventDefault()
        setScope(s => {
          const next = s === 'all' ? 'jobs' : s === 'jobs' ? 'clients' : s === 'clients' ? 'staff' : 'all'
          if (query.trim()) doSearch(query, next)
          return next
        })
        setSelectedIndex(0)
      }
      if (e.key === 'Enter') {
        if (matchedDate) { navigate(`/jobs?dateFrom=${matchedDate.from}&dateTo=${matchedDate.to}`); onClose(); return }
        if (matchedPage && !allItems.length) { navigate(matchedPage); onClose(); return }
        if (allItems[selectedIndex]) handleSelect(allItems[selectedIndex])
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [allItems, selectedIndex])

  const handleSelect = (item: { type: string, data: any }) => {
    saveRecentSearch(query)
    if (item.type === 'job') {
      setSessionViewed(prev => [item.data, ...prev.filter(j => j.id !== item.data.id)].slice(0, 5))
      navigate(`/jobs?openJob=${item.data.id}`)
    } else if (item.type === 'client') {
      navigate(`/clients?open=${item.data.id}`)
    } else if (item.type === 'staff') {
      navigate(`/staff?open=${item.data.id}`)
    }
    onClose()
  }

  const handleQuickAction = (action: string) => {
    if (action === 'unallocated') navigate('/jobs?status=Unallocated')
    else if (action === 'sendToQC') navigate('/jobs?status=Send+to+QC')
    else if (action === 'today') navigate('/jobs?today=1')
    else if (action === 'myJobs') navigate('/jobs?myJobs=1')
    else if (action === 'expiring') navigate('/revenue')
    onClose()
  }

  const hasResults = allItems.length > 0
  const showEmpty = query.trim() && !loading && !hasResults

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex flex-col items-center"
      onClick={onClose}>

      {/* Orange arc gradient background — center-top focus */}
      <div className="absolute inset-x-0 top-0 h-[55%] pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 100% 100% at 50% 0%, rgba(244,107,32,0.95) 0%, rgba(244,107,32,0.7) 30%, rgba(244,107,32,0.2) 65%, rgba(244,107,32,0) 100%)'
        }} />

      {/* Dark overlay for rest of screen */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-2xl px-4 pt-16 sm:pt-20"
        onClick={e => e.stopPropagation()}>

        {/* Search bar */}
        <div className="relative mb-4">
          {/* Orange glow behind search bar */}
          <div className="absolute inset-0 rounded-full blur-xl opacity-40"
            style={{ background: 'radial-gradient(ellipse, rgba(244,107,32,0.8) 0%, transparent 70%)' }} />
          <div className="relative flex items-center bg-white dark:bg-[#162840] rounded-full shadow-2xl border-2 border-orange-300/50 px-5 py-3.5 gap-3 [&_input]:text-navy-900 [&_input]:dark:text-white">
            {/* Ghost text suggestion overlay */}
            {ghostSuggestion && (
              <div className="absolute left-0 right-0 px-5 py-3.5 pointer-events-none flex items-center gap-3">
                <span className="w-5 h-5 flex-shrink-0 opacity-0" />
                <span className="text-base whitespace-pre flex-1 overflow-hidden">
                  <span className="opacity-0">{query}</span>
                  <span className="text-gray-400 dark:text-gray-500">{ghostSuggestion.slice(query.length)}</span>
                </span>
              </div>
            )}
            {loading ? (
              <Loader2 size={18} className="text-orange-400 animate-spin flex-shrink-0" />
            ) : (
              <Search size={18} className="text-orange-400 flex-shrink-0" />
            )}
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={window.innerWidth < 640 ? "Search jobs, clients..." : "Search jobs, clients, staff, status (e.g. unallocated, qc)..."}
              className="flex-1 min-w-0 bg-transparent placeholder-gray-400 dark:placeholder-gray-500 text-sm sm:text-base focus:outline-none"
              style={{ color: '#0a2240', caretColor: '#f97316' }}
            />
            {ghostSuggestion && (
              <span className="flex-shrink-0 text-[10px] text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-600 rounded px-1.5 py-0.5 font-mono whitespace-nowrap">
                Tab ↵
              </span>
            )}
            <div className="flex items-center gap-2 flex-shrink-0">
              {query && (
                <button onClick={() => setQuery('')}
                  className="text-gray-400 hover:text-gray-600 transition-colors">
                  <X size={15} />
                </button>
              )}
              <kbd className="text-[10px] text-gray-400 bg-gray-100 dark:bg-[#162840] px-2 py-0.5 rounded-lg font-mono">ESC</kbd>
            </div>
          </div>

          {/* Suggestions dropdown */}
          {showSuggestions && query.trim() && (
            <div className="absolute left-0 right-0 top-full mt-2 bg-white dark:bg-[#0f2137] rounded-2xl shadow-2xl border border-gray-100 dark:border-[#1e3a5f] z-50 overflow-hidden">
              <div className="px-3 py-2 border-b border-gray-50 dark:border-[#162840] flex items-center justify-between">
                <span className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold">Suggestions</span>
                <span className="text-[10px] text-gray-400">↑↓ navigate · Enter to search · Esc to close</span>
              </div>
              {suggestions.map((s, i) => (
                <div key={i}
                  onClick={() => {
                    const text = s.text
                    // Handle navigation suggestions directly
                    if (text.startsWith('go to ')) {
                      const keyword = text.replace('go to ', '').trim()
                      const pageEntry = PAGE_SHORTCUTS.find(p => p.keyword === keyword)
                      if (pageEntry) { navigate(pageEntry.path); onClose(); return }
                    }
                    setQuery(text)
                    setShowSuggestions(false)
                    doSearch(text, scopeRef.current)
                    saveRecentSearch(text)
                  }}
                  className={`px-4 py-2.5 flex items-center gap-3 cursor-pointer transition-colors ${selectedSuggestionIndex === i ? 'bg-orange-50 dark:bg-orange-900/10' : 'hover:bg-gray-50 dark:hover:bg-[#162840]'}`}>
                  <span className="w-5 flex-shrink-0 flex items-center justify-center text-gray-400">
                    {s.icon === 'status' && <Zap size={13} className="text-orange-400" />}
                    {s.icon === 'time' && <Calendar size={13} className="text-blue-400" />}
                    {s.icon === 'product' && <Palette size={13} className="text-green-400" />}
                    {s.icon === 'myjobs' && <UserCircle size={13} className="text-teal-400" />}
                    {s.icon === 'client' && <Building2 size={13} className="text-blue-400" />}
                    {s.icon === 'staff' && <Users size={13} className="text-purple-400" />}
                    {s.icon === 'navigate' && <Navigation size={13} className="text-gray-400" />}
                    {s.icon === 'sub' && <CheckCircle size={13} className="text-green-400" />}
                    {s.icon === 'recent' && <Clock size={13} className="text-gray-400" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-navy-800 dark:text-white">
                      {s.text.toLowerCase().startsWith(query.toLowerCase())
                        ? <><mark className="bg-orange-200 dark:bg-orange-500/40 text-orange-800 dark:text-orange-200 rounded px-0.5 font-semibold not-italic">{s.text.slice(0, query.length)}</mark>{s.text.slice(query.length)}</>
                        : s.text
                      }
                    </p>
                  </div>
                  <span className="text-[10px] text-gray-400 flex-shrink-0 bg-gray-50 dark:bg-[#1e3a5f] px-2 py-0.5 rounded-full">{s.category}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Scope filter pills */}
        {query.trim() && (
          <div className="flex items-center gap-2 mb-2 px-1">
            {(['all', 'jobs', 'clients', 'staff'] as const).map(s => (
              <button key={s} onClick={() => { setScope(s); setSelectedIndex(0); if (query.trim()) doSearch(query, s) }}
                className={`text-xs px-3 py-1 rounded-full font-medium transition-colors capitalize ${scope === s ? 'bg-white text-orange-600 shadow-sm' : 'text-white/70 hover:text-white'}`}>
                {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                {s === 'jobs' && results.jobs.length > 0 && <span className="ml-1 opacity-70">({results.jobs.length})</span>}
                {s === 'clients' && results.clients.length > 0 && <span className="ml-1 opacity-70">({results.clients.length})</span>}
                {s === 'staff' && results.staff.length > 0 && <span className="ml-1 opacity-70">({results.staff.length})</span>}
              </button>
            ))}
            <span className="text-[10px] text-white/40 ml-auto">Tab to switch</span>
          </div>
        )}

        {/* Results / Quick actions */}
        <div className="bg-white dark:bg-[#0f2137] rounded-2xl shadow-2xl border border-gray-100 dark:border-[#1e3450] overflow-hidden max-h-[55vh] overflow-y-auto">

        {/* Combined filter indicator */}
          {combinedFilter.isCombined && query.trim() && (
            <div className="px-4 py-2 bg-purple-50 dark:bg-purple-900/10 border-b border-purple-100 dark:border-purple-900/20 flex items-center gap-2">
              <span className="text-[10px] text-purple-600 dark:text-purple-300 font-medium">Combined filter:</span>
              {combinedFilter.clientQuery && <span className="text-[10px] bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-200 px-2 py-0.5 rounded-full">"{combinedFilter.clientQuery}"</span>}
              {combinedFilter.detectedStatus && <span className="text-[10px] bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-200 px-2 py-0.5 rounded-full">{combinedFilter.detectedStatus}</span>}
              {combinedFilter.detectedType && <span className="text-[10px] bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-200 px-2 py-0.5 rounded-full">{combinedFilter.detectedType}</span>}
            </div>
          )}

        {/* Date shortcut match */}
          {matchedDate && (
            <div className="p-3 border-b border-gray-50 dark:border-[#1e3450]">
              <button onClick={() => { navigate(`/jobs?dateFrom=${matchedDate.from}&dateTo=${matchedDate.to}`); onClose() }}
                className="w-full flex items-center gap-3 px-3 py-2.5 bg-blue-50 dark:bg-blue-900/20 rounded-xl hover:bg-blue-100 transition-colors">
                <Clock size={14} className="text-blue-500 flex-shrink-0" />
                <span className="text-sm font-medium text-blue-600 dark:text-blue-300">Show jobs from {matchedDate.label}</span>
                <kbd className="ml-auto text-[10px] text-blue-400 bg-blue-100 dark:bg-blue-900/40 px-2 py-0.5 rounded font-mono">↵</kbd>
              </button>
            </div>
          )}

          {/* Page navigation match */}
          {matchedPage && (
            <div className="p-3 border-b border-gray-50 dark:border-[#1e3450]">
              <button onClick={() => { navigate(matchedPage); onClose() }}
                className="w-full flex items-center gap-3 px-3 py-2.5 bg-orange-50 dark:bg-orange-900/20 rounded-xl hover:bg-orange-100 transition-colors">
                <ArrowRight size={14} className="text-orange-500 flex-shrink-0" />
                <span className="text-sm font-medium text-orange-600 dark:text-orange-300">Go to {query.trim().charAt(0).toUpperCase() + query.trim().slice(1)} page</span>
                <kbd className="ml-auto text-[10px] text-orange-400 bg-orange-100 dark:bg-orange-900/40 px-2 py-0.5 rounded font-mono">↵</kbd>
              </button>
            </div>
          )}

          {/* Quick actions — shown when no query */}
          {!query.trim() && (
            <div className="p-4">
              {/* Recent searches */}
              {recentSearches.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Recent Searches</p>
                    <button onClick={clearRecentSearches} className="text-[10px] text-gray-400 hover:text-red-400 transition-colors">Clear</button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {recentSearches.map(s => (
                      <button key={s} onClick={() => setQuery(s)}
                        className="flex items-center gap-1.5 text-xs bg-gray-50 dark:bg-[#162840] text-gray-600 dark:text-gray-300 px-3 py-1.5 rounded-full border border-gray-100 dark:border-[#1e3450] hover:border-orange-300 hover:text-orange-500 transition-colors">
                        <Clock size={10} /> {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Recently viewed jobs */}
              {sessionViewed.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Recently Viewed</p>
                  <div className="space-y-1">
                    {sessionViewed.map(job => (
                      <div key={job.id} onClick={() => { navigate(`/jobs?openJob=${job.id}`); onClose() }}
                        className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-gray-50 dark:hover:bg-[#162840] cursor-pointer transition-colors group">
                        <div className="w-1 h-6 bg-orange-300 rounded-full flex-shrink-0" />
                        <span className="text-xs font-mono text-orange-500"><Highlight text={job.jobCode} query={query} /></span>
                        <span className="text-xs text-navy-700 dark:text-gray-300 truncate flex-1">{job.displayTitle}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${STATUS_COLORS[job.status] || 'bg-gray-100 text-gray-500'}`}>{job.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Quick Actions</p>
              {quickLoading ? (
                <div className="flex items-center gap-2 text-gray-400 text-sm py-2">
                  <Loader2 size={14} className="animate-spin" /> Loading...
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {!isArtist && quickActions.unallocated !== undefined && (
                    <button onClick={() => handleQuickAction('unallocated')}
                      className="flex items-center gap-2 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300 px-3 py-2 rounded-xl text-sm font-medium hover:bg-yellow-100 transition-colors">
                      <AlertCircle size={14} /> Unallocated
                      <span className="bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200 text-xs px-1.5 py-0.5 rounded-full font-bold">{quickActions.unallocated}</span>
                    </button>
                  )}
                  {!isArtist && quickActions.sendToQC !== undefined && (
                    <button onClick={() => handleQuickAction('sendToQC')}
                      className="flex items-center gap-2 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 px-3 py-2 rounded-xl text-sm font-medium hover:bg-purple-100 transition-colors">
                      <CheckCircle size={14} /> Send to QC
                      <span className="bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-200 text-xs px-1.5 py-0.5 rounded-full font-bold">{quickActions.sendToQC}</span>
                    </button>
                  )}
                  {!isArtist && quickActions.todayJobs !== undefined && (
                    <button onClick={() => handleQuickAction('today')}
                      className="flex items-center gap-2 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 px-3 py-2 rounded-xl text-sm font-medium hover:bg-orange-100 transition-colors">
                      <Clock size={14} /> Today's Jobs
                      <span className="bg-orange-200 dark:bg-orange-800 text-orange-800 dark:text-orange-200 text-xs px-1.5 py-0.5 rounded-full font-bold">{quickActions.todayJobs}</span>
                    </button>
                  )}
                  {isArtist && quickActions.myJobs !== undefined && (
                    <button onClick={() => handleQuickAction('myJobs')}
                      className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 px-3 py-2 rounded-xl text-sm font-medium hover:bg-blue-100 transition-colors">
                      <Briefcase size={14} /> My Active Jobs
                      <span className="bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 text-xs px-1.5 py-0.5 rounded-full font-bold">{quickActions.myJobs}</span>
                    </button>
                  )}
                  {isAdmin && quickActions.expiringSubs !== undefined && quickActions.expiringSubs > 0 && (
                    <button onClick={() => handleQuickAction('expiring')}
                      className="flex items-center gap-2 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-3 py-2 rounded-xl text-sm font-medium hover:bg-red-100 transition-colors">
                      <AlertCircle size={14} /> Expiring Subs
                      <span className="bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200 text-xs px-1.5 py-0.5 rounded-full font-bold">{quickActions.expiringSubs}</span>
                    </button>
                  )}
                </div>
              )}
              <p className="text-xs text-gray-400 mt-4 text-center">Type to search jobs, clients, and staff</p>
            </div>
          )}

          {/* Search results */}
          {query.trim() && !loading && hasResults && (
            <div className="divide-y divide-gray-50 dark:divide-[#1e3450]">
              {/* Jobs */}
              {results.jobs.length > 0 && (
                <div>
                  <p className="px-4 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide bg-gray-50/70 dark:bg-[#162840] flex items-center gap-1.5">
                    <Briefcase size={10} /> Jobs
                  </p>
                  {results.jobs.map((job: any, i: number) => {
                    const globalIdx = i
                    return (
                      <div key={job.id}
                        onClick={() => handleSelect({ type: 'job', data: job })}
                        onMouseEnter={() => {
                          clearTimeout(hoverTimeout.current)
                          hoverTimeout.current = setTimeout(() => { setHoveredJobId(job.id); fetchJobPreview(job.id) }, 500)
                        }}
                        onMouseLeave={() => { clearTimeout(hoverTimeout.current); setHoveredJobId(null); setHoveredJobData(null) }}
                        className={`relative px-4 py-3 flex items-center gap-3 cursor-pointer transition-colors ${selectedIndex === globalIdx ? 'bg-orange-50 dark:bg-orange-900/10' : 'hover:bg-gray-50 dark:hover:bg-[#162840]'}`}>
                        <div className="w-1 h-8 bg-orange-400 rounded-full flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-xs font-mono text-orange-500">{job.jobCode}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${STATUS_COLORS[job.status] || 'bg-gray-100 text-gray-500'}`}>{job.status}</span>
                            {job.productType && <span className="text-[10px] bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-300 px-1.5 py-0.5 rounded-full">{job.productType}</span>}
                          </div>
                          <p className="text-sm font-medium text-navy-800 dark:text-white truncate"><Highlight text={job.displayTitle} query={combinedFilter.isCombined ? combinedFilter.clientQuery : query} /></p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-[10px] text-gray-400">{job.client}{job.allocatedTo ? ` · ${job.allocatedTo}` : ''}</p>
                            {job.requestType && (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${job.requestType === 'revision' ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-500' : 'bg-orange-50 dark:bg-orange-900/20 text-orange-500'}`}>
                                {job.requestType}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button onClick={e => copyJobCode(e, job.jobCode, job.id)}
                            className="p-1 rounded text-gray-300 hover:text-orange-500 transition-colors"
                            title="Copy job code">
                            {copiedId === job.id ? (
                              <CheckCircle size={12} className="text-green-500" />
                            ) : (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                            )}
                          </button>
                          <ArrowRight size={14} className="text-gray-300" />
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Clients */}
              {results.clients.length > 0 && (
                <div>
                  <p className="px-4 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide bg-gray-50/70 dark:bg-[#162840] flex items-center gap-1.5">
                    <Building2 size={10} /> Clients
                  </p>
                  {results.clients.map((client: any, i: number) => {
                    const globalIdx = results.jobs.length + i
                    return (
                      <div key={client.id}
                        onClick={() => handleSelect({ type: 'client', data: client })}
                        className={`px-4 py-3 flex items-center gap-3 cursor-pointer transition-colors ${selectedIndex === globalIdx ? 'bg-orange-50 dark:bg-orange-900/10' : 'hover:bg-gray-50 dark:hover:bg-[#162840]'}`}>
                        <div className="w-1 h-8 bg-blue-400 rounded-full flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-navy-800 dark:text-white truncate">
                            <Highlight text={client.companyName} query={query} />
                          </p>
                          <p className="text-[10px] text-gray-400">
                            <Highlight text={client.abbreviation} query={query} /> · {client.email}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {client.abbreviation && (
                            <button onClick={e => {
                              e.stopPropagation()
                              navigate(`/jobs?clientId=${client.id}`)
                              onClose()
                            }}
                              className="text-[10px] text-blue-500 hover:text-blue-700 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded-lg flex items-center gap-1 transition-colors whitespace-nowrap">
                              <Briefcase size={10} /> Their jobs →
                            </button>
                          )}
                        {client.subscriptionStatus && (
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${client.subscriptionStatus === 'active' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>
                              {client.subscriptionStatus}
                            </span>
                          )}
                          <ArrowRight size={14} className="text-gray-300 flex-shrink-0" />
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Staff */}
              {results.staff.length > 0 && (
                <div>
                  <p className="px-4 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide bg-gray-50/70 dark:bg-[#162840] flex items-center gap-1.5">
                    <Users size={10} /> Staff
                  </p>
                  {results.staff.map((member: any, i: number) => {
                    const globalIdx = results.jobs.length + results.clients.length + i
                    return (
                      <div key={member.id}
                        onClick={() => handleSelect({ type: 'staff', data: member })}
                        className={`px-4 py-3 flex items-center gap-3 cursor-pointer transition-colors ${selectedIndex === globalIdx ? 'bg-orange-50 dark:bg-orange-900/10' : 'hover:bg-gray-50 dark:hover:bg-[#162840]'}`}>
                        <div className="w-1 h-8 bg-green-400 rounded-full flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-navy-800 dark:text-white truncate"><Highlight text={member.name} query={query} /></p>
                          <p className="text-[10px] text-gray-400">{member.role} · <Highlight text={member.email} query={query} /></p>
                        </div>
                        <ArrowRight size={14} className="text-gray-300 flex-shrink-0" />
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* Empty state */}
          {showEmpty && (
            <div className="py-12 text-center text-gray-400">
              <Search size={32} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">No results for "{query}"</p>
              <p className="text-xs mt-1">Try a job code, client name, or staff name</p>
            </div>
          )}
        </div>

        {/* Job preview on hover — outside scroll container */}
        {hoveredJobId && hoveredJobData && (
          <div className="mt-2 bg-white dark:bg-[#162840] rounded-xl shadow-2xl border border-orange-100 dark:border-[#1e3450] p-4 z-50"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-mono text-orange-500">{hoveredJobData.jobCode}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${STATUS_COLORS[hoveredJobData.status] || 'bg-gray-100 text-gray-500'}`}>{hoveredJobData.status}</span>
              {hoveredJobData.productType && <span className="text-[10px] bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-300 px-1.5 py-0.5 rounded-full">{hoveredJobData.productType}</span>}
            </div>
            <p className="text-sm font-medium text-navy-800 dark:text-white mb-2">{hoveredJobData.displayTitle}</p>
            {hoveredJobData.cleanedInstructions && (
              <div className="mb-2">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Instructions</p>
                <p className="text-xs text-gray-600 dark:text-gray-300 line-clamp-4 leading-relaxed whitespace-pre-line">
                  {hoveredJobData.cleanedInstructions.slice(0, 300)}{hoveredJobData.cleanedInstructions.length > 300 ? '...' : ''}
                </p>
              </div>
            )}
            <div className="flex items-center gap-3 text-[10px] text-gray-400 pt-2 border-t border-gray-50 dark:border-[#1e3450]">
              {hoveredJobData.allocatedToName && <span>👤 {hoveredJobData.allocatedToName}</span>}
              {hoveredJobData.client?.abbreviation && <span>🏢 {hoveredJobData.client.abbreviation}</span>}
              <span className="ml-auto">{new Date(hoveredJobData.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
            </div>
          </div>
        )}

        {/* Footer hints */}
        <div className="flex items-center justify-between mt-3 px-1">
          <div className="flex items-center gap-4">
            {['↑↓ Navigate', '↵ Open', 'Tab Filter', 'ESC Close'].map(hint => (
              <span key={hint} className="text-[10px] text-white/60">{hint}</span>
            ))}
          </div>
          <button onClick={e => { e.stopPropagation(); setShowHints(v => !v) }}
            className="text-[10px] text-white/60 hover:text-white/90 flex items-center gap-1 transition-colors">
            <span>?</span> Search tips
          </button>
        </div>

        {/* Search hints tooltip */}
        {showHints && (
          <div className="mt-2 bg-white dark:bg-[#0f2137] rounded-2xl shadow-2xl border border-gray-100 dark:border-[#1e3450] p-4 max-h-[60vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-semibold text-navy-800 dark:text-white">What can you search?</p>
              <span className="text-[10px] text-gray-400">Click any example to search</span>
            </div>

            <div className="space-y-5">

              {/* Status queries — hide from client */}
              {user?.roleName !== 'client' && (
              <div>
                <p className="text-[10px] font-semibold text-orange-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <Zap size={11} /> Status Queries
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    'unallocated', 'unallocated jobs',
                    'jobs in process', 'in process',
                    'send to qc', 'send to qc jobs',
                    'completed jobs', 'paused jobs',
                    'carryover jobs', 'correction jobs',
                    'query sent', 'dismissed jobs',
                  ].map(s => (
                    <button key={s} onClick={() => { setQuery(s); setShowHints(false); doSearch(s, scopeRef.current) }}
                      className="text-[11px] bg-orange-50 dark:bg-orange-900/10 text-orange-600 dark:text-orange-400 px-2.5 py-1 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/20 transition-colors font-mono">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              )}

              {/* Time queries — hide from client */}
              {user?.roleName !== 'client' && (
              <div>
                <p className="text-[10px] font-semibold text-blue-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <Calendar size={11} /> Time Based
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    "today's jobs", "yesterday's jobs",
                    'this week jobs', 'last week jobs',
                    'this month jobs', 'my jobs today',
                    'my jobs this week',
                  ].map(s => (
                    <button key={s} onClick={() => { setQuery(s); setShowHints(false); doSearch(s, scopeRef.current) }}
                      className="text-[11px] bg-blue-50 dark:bg-blue-900/10 text-blue-600 dark:text-blue-400 px-2.5 py-1 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/20 transition-colors font-mono">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              )}

              {/* Combined queries — admin/tl only */}
              {['admin', 'tl'].includes(user?.roleName || '') && (
              <div>
                <p className="text-[10px] font-semibold text-purple-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <Link2 size={11} /> Combined Queries
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    'ABC unallocated', 'ABC completed this week',
                    'ABC in process', 'XYZ fresh today',
                    'Priya completed', 'Priya this week',
                    'Arun unallocated', 'ABC fresh jobs',
                  ].map(s => (
                    <button key={s} onClick={() => { setQuery(s); setShowHints(false); doSearch(s, scopeRef.current) }}
                      className="text-[11px] bg-purple-50 dark:bg-purple-900/10 text-purple-600 dark:text-purple-400 px-2.5 py-1 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/20 transition-colors font-mono">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              )}

              {/* Product type queries — hide from client */}
              {user?.roleName !== 'client' && (
              <div>
                <p className="text-[10px] font-semibold text-green-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <Palette size={11} /> Product Types
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    'embroidery jobs', 'medal jobs',
                    'lapel pin jobs', 'patch jobs',
                    'lanyard jobs', 'keychain jobs',
                    'belt buckle jobs', 'charm jobs',
                    'trophy jobs', 'coin jobs', 'mug jobs',
                  ].map(s => (
                    <button key={s} onClick={() => { setQuery(s); setShowHints(false); doSearch(s, scopeRef.current) }}
                      className="text-[11px] bg-green-50 dark:bg-green-900/10 text-green-600 dark:text-green-400 px-2.5 py-1 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/20 transition-colors font-mono">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              )}

              {/* My jobs — hide from client */}
              {user?.roleName !== 'client' && (
              <div>
                <p className="text-[10px] font-semibold text-teal-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <UserCircle size={11} /> My Jobs
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    'my jobs', 'my jobs today',
                    'my jobs this week', 'my unallocated',
                    'my completed', 'my jobs in process',
                  ].map(s => (
                    <button key={s} onClick={() => { setQuery(s); setShowHints(false); doSearch(s, scopeRef.current) }}
                      className="text-[11px] bg-teal-50 dark:bg-teal-900/10 text-teal-600 dark:text-teal-400 px-2.5 py-1 rounded-lg hover:bg-teal-100 dark:hover:bg-teal-900/20 transition-colors font-mono">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              )}

              {/* Navigate — role based */}
              <div>
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <Navigation size={11} /> Navigate to Page
                </p>
                <div className="flex flex-wrap gap-2">
                  {(() => {
                    const role = user?.roleName || ''
                    const pages: string[] = []
                    // Jobs — all roles except client (they have their own view)
                    if (role !== 'client') pages.push('go to jobs')
                    // Dashboard
                    pages.push('go to dashboard')
                    // Clients — admin, tl only
                    if (['admin', 'tl'].includes(role)) pages.push('go to clients')
                    // Staff — admin, tl, hr
                    if (['admin', 'tl', 'hr'].includes(role)) pages.push('go to staff')
                    // Reports — admin, tl
                    if (['admin', 'tl'].includes(role)) pages.push('go to reports')
                    // Revenue — admin only
                    if (role === 'admin') pages.push('go to revenue')
                    // Finance — admin only
                    if (role === 'admin') pages.push('go to finance')
                    // Settings — admin only
                    if (role === 'admin') pages.push('go to settings')
                    // Support — admin only
                    if (role === 'admin') pages.push('go to support')
                    // Attendance — all staff roles
                    if (role !== 'client') pages.push('go to attendance')
                    // Leave — all staff roles
                    if (role !== 'client') pages.push('go to leave')
                    // Performance — all staff roles
                    if (role !== 'client') pages.push('go to performance')
                    // Announcements — all roles
                    pages.push('go to announcements')
                    // Deals — admin, tl
                    if (['admin', 'tl'].includes(role)) pages.push('go to deals')
                    return pages
                  })().map(s => (
                    <button key={s} onClick={() => {
                      const keyword = s.replace('go to ', '').trim()
                      const pageEntry = PAGE_SHORTCUTS.find(p => p.keyword === keyword)
                      if (pageEntry) { navigate(pageEntry.path); onClose(); return }
                      setQuery(s); setShowHints(false); doSearch(s, scopeRef.current)
                    }}
                      className="text-[11px] bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 px-2.5 py-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors font-mono">
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Exact search */}
              <div>
                <p className="text-[10px] font-semibold text-red-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <ScanSearch size={11} /> Exact Search
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    'MTY-2026-0480', 'soft enamel',
                    '3.5 inch',
                    'Priya', 'ABC Company',
                  ].map(s => (
                    <button key={s} onClick={() => { setQuery(s); setShowHints(false); doSearch(s, scopeRef.current) }}
                      className="text-[11px] bg-red-50 dark:bg-red-900/10 text-red-500 dark:text-red-400 px-2.5 py-1 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors font-mono">
                      {s}
                    </button>
                  ))}
                </div>
              </div>

            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}